import React, { useEffect, useState } from 'react'
import { useFormState } from 'react-hook-form'
import cn from 'classnames'
import { Button, IconClose, Typography } from '@grammarly/badger-ui'
import { Icon, InterfaceOkIcon } from '@grammarly/design-system'
import { FormattedMessage, useIntl } from 'react-intl'

import { TypeComponentTextInput } from '../../../lib/types/component-text-input'
import { ContentProps } from '../../renderer'
import { InputError } from '../../inputs/hooks'
import { charactersToWords } from '../../inputs/validations'
import { Modal } from '../../ui-lib/modal'
import { SignUpModalContent } from '../../ui-lib/modal/signup'

import { SignUpProductRecButton } from '../../item/cta-button/signup-product-recommendation'
import { ButtonColor } from '../../ui-lib/button'
import { useAIDetector } from './ai-detector-context'
import { AIDetectorValuePropList } from './utils'
import { gnarSpec } from '../../../lib/tracking/gnar'
import { useExperimentService } from '../../../lib/hooks'
import { FreeUserTrialModal } from '../../ui-lib/modal/free-user-trial-modal'

import styles from './ai-detector-shared.module.scss'

const { HS, TS } = Typography

interface AIDetectorBaseModalProps {
  modalClassname: string
  isModalOpen: boolean
  setIsModalOpen: (isOpen: boolean) => void
  children: React.ReactNode
}

const AIDetectorBaseModal: React.FC<AIDetectorBaseModalProps> = ({
  modalClassname,
  isModalOpen,
  setIsModalOpen,
  children,
}) => {
  const intl = useIntl()
  const { pageId, slug } = useAIDetector()
  return (
    <Modal className={modalClassname} isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)}>
      <div className={cn(styles.card, styles.signupWall)}>
        <Button
          className={styles.signupWallClose}
          padding="none"
          onClick={() => {
            gnarSpec.aiDetectorCtaClick('paywallClose', pageId, slug, 'paywall')
            setIsModalOpen(false)
          }}
          aria-label={intl.formatMessage({
            id: 'global.uiAction.close',
            description: 'Text for CTA that closes an element',
            defaultMessage: 'Close',
          })}
        >
          <IconClose aria-hidden={true} />
        </Button>
        {children}
      </div>
    </Modal>
  )
}

export const AIDetectorTextLimitModal: React.FC<ContentProps<TypeComponentTextInput>> = ({
  structure: {
    fields: { name, maxLength, useWordLimit },
  },
}) => {
  const { pageId, slug } = useAIDetector()
  const [isWallOpen, setIsWallOpen] = useState(false)
  const [hasOpenedWall, setHasOpenedWall] = useState(false)
  const { errors, isValidating } = useFormState({ name })

  useEffect(() => {
    if (errors[name]?.message?.toString() === InputError.ExceededCharacterLimit && !hasOpenedWall) {
      setIsWallOpen(true)
      setHasOpenedWall(true)
      gnarSpec.aiDetectorWallShow(pageId, slug, 'textLimit')
    }
  }, [setIsWallOpen, errors, name, hasOpenedWall, setHasOpenedWall, isValidating, pageId, slug])

  if (!maxLength) {
    return null
  }

  const limitType = useWordLimit ? 'word' : 'character'
  const limit = useWordLimit ? maxLength / charactersToWords : maxLength

  return (
    <AIDetectorBaseModal
      modalClassname={styles.textLimitWallModal}
      isModalOpen={isWallOpen}
      setIsModalOpen={setIsWallOpen}
    >
      <HS as="h2">
        <FormattedMessage
          id="aiDetector.textLimitModal.heading"
          description="AI Detector - Text Limit Modal - Heading"
          defaultMessage="This text exceeds the {limitType} limit"
          values={{ limitType }}
        />
      </HS>
      <TS as="div" className={styles.cardDescription}>
        <FormattedMessage
          id="aiDetector.textLimitModal.copy"
          description="AI Detector - Text Limit Modal - Copy"
          defaultMessage="You can check text that is {limit} {limitType}s or less with this tool. Install Grammarly to detect AI in longer text."
          values={{ limit, limitType }}
        />
      </TS>
      <SignUpProductRecButton
        color={ButtonColor.WDK24PrimaryLightBg}
        className={styles.textLimitButton}
        text={
          <strong>
            <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
          </strong>
        }
        showArrow
        placement="Modal"
      />
    </AIDetectorBaseModal>
  )
}

export const AIDetectorUsageModal = () => {
  const { isAnonymous, showUsageWall, setShowUsageWall, pageId, slug } = useAIDetector()
  const { isGoogleCtaSignupWallV2 } = useExperimentService()

  const isGoogleCtaSignupWallV2Enabled = showUsageWall && isGoogleCtaSignupWallV2(isAnonymous)

  useEffect(() => {
    if (showUsageWall) {
      gnarSpec.aiDetectorWallShow(pageId, slug, 'usage')
    }
  }, [pageId, slug, showUsageWall])

  return (
    <AIDetectorBaseModal
      modalClassname={styles.usageLimitWallModal}
      isModalOpen={showUsageWall}
      setIsModalOpen={setShowUsageWall}
    >
      {isGoogleCtaSignupWallV2Enabled ? (
        <SignUpModalContent />
      ) : (
        <>
          <HS as="h2">
            <FormattedMessage
              id="aiDetector.usageModal.heading"
              description="AI Detector - Usage Modal - Heading"
              defaultMessage="Get unlimited AI detection and more"
            />
          </HS>
          <ul className={styles.usageWallChecklist}>
            {AIDetectorValuePropList.map((valueProp) => (
              <TS as="li" key={valueProp}>
                <span className={styles.usageWallChecklistIcon}>
                  <Icon icon={InterfaceOkIcon} accessibilityLabel="" size="large" />
                </span>
                {valueProp}
              </TS>
            ))}
          </ul>
          <SignUpProductRecButton
            color={ButtonColor.WDK24PrimaryLightBg}
            className={styles.usageLimitButton}
            text={
              <strong>
                <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
              </strong>
            }
            showArrow
          />
        </>
      )}
    </AIDetectorBaseModal>
  )
}

export const AIDetectorFreeUserTrialModal = () => {
  const { showTrialModal, setShowTrialModal, appElementForModal } = useAIDetector()

  return (
    <FreeUserTrialModal
      show={showTrialModal}
      close={() => setShowTrialModal(false)}
      appElementForModal={appElementForModal}
    />
  )
}
